import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, XCircle, RefreshCw } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { PageTitle, Panel } from "@/components/admin/AdminLayout";

const STYLE = {
  ok: ["Done", CheckCircle2, "text-emerald-700 bg-emerald-50 border-emerald-200"],
  pending: ["To do", Clock, "text-amber-800 bg-amber-50 border-amber-200"],
  failed: ["Problem", XCircle, "text-red-700 bg-red-50 border-red-200"],
};

const Row = ({ i }) => {
  const [label, Icon, cls] = STYLE[i.status];
  return (
    <li className="py-3 flex gap-3 border-t border-slate-100 first:border-0" data-testid={`launch-item-${i.key}`}>
      <span className={`shrink-0 h-7 px-2 rounded-md border text-xs font-bold flex items-center gap-1 ${cls}`} data-testid={`launch-status-${i.key}`}><Icon size={14} /> {label}</span>
      <div className="text-sm">
        <p className="font-semibold">{i.label}</p>
        <p className="text-slate-600">{i.detail}</p>
        {i.action && <p className="text-slate-500 mt-0.5"><span className="font-semibold">What to do:</span> {i.action}</p>}
      </div>
    </li>
  );
};

export default function LaunchChecklist() {
  const { data, isFetching, refetch } = useQuery({ queryKey: ["launch"], queryFn: () => api.get("/admin/launch-checklist").then((r) => r.data) });
  return (
    <div className="space-y-6" data-testid="launch-page">
      <PageTitle title="Launch day checklist" sub="Live checks of your setup. Only statuses are shown - key values are never displayed." />
      <div className="flex flex-wrap items-center gap-3">
        {data && Object.entries(STYLE).map(([k, [label]]) => (
          <span key={k} className={`text-sm font-bold px-3 py-1.5 rounded-lg border ${STYLE[k][2]}`} data-testid={`launch-summary-${k}`}>{data.summary[k]} {label}</span>
        ))}
        <button onClick={() => refetch()} disabled={isFetching} className="ml-auto px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-bold flex items-center gap-1.5 disabled:opacity-40" data-testid="launch-recheck-button">
          <RefreshCw size={15} className={isFetching ? "animate-spin" : ""} /> Check again
        </button>
      </div>
      {!data && <div className="h-64 rounded-xl bg-slate-200 animate-pulse" />}
      {data?.sections.map((s) => (
        <Panel key={s.title} title={s.title} testid={`launch-section-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
          <ul>{s.items.map((i) => <Row key={i.key} i={i} />)}</ul>
        </Panel>
      ))}
      {data && <p className="text-xs text-slate-400">Last checked {fmtDateTime(data.checked_at)}</p>}
    </div>
  );
}
